import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Euro, Phone, TrendingUp, FileText, Mail, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [tab, setTab] = useState("overview");
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, invoicesRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/invoices`),
        axios.get(`${API}/admin/contact-submissions`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setInvoices(invoicesRes.data);
      setSubmissions(subsRes.data);
    } catch (e) {
      console.error("Admin fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    }).format(new Date(d));
  };

  if (loading) {
    return <div className="animate-pulse space-y-4"><div className="h-14 bg-slate-200 rounded-lg" /></div>;
  }

  const tabs = [
    { id: "overview", label: "Übersicht" },
    { id: "users", label: "Benutzer" },
    { id: "invoices", label: "Rechnungen" },
    { id: "submissions", label: "Kontaktanfragen" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Admin-Bereich</h1>
        <p className="text-slate-500 mt-1">Benutzerverwaltung, Rechnungen und Kontaktanfragen</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? "bg-white text-[#405189] shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`} data-testid={`admin-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white shadow-sm border border-slate-200/60">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Benutzer</span>
              </div>
              <div className="flex items-end justify-between">
                <h3 className="text-[22px] font-bold text-slate-800" data-testid="admin-total-users">{stats.total_users}</h3>
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border border-slate-200/60">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Umsatz</span>
              </div>
              <div className="flex items-end justify-between">
                <h3 className="text-[22px] font-bold text-slate-800">{stats.total_revenue.toFixed(2)} €</h3>
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border border-slate-200/60">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gesamtanrufe</span>
              </div>
              <div className="flex items-end justify-between">
                <h3 className="text-[22px] font-bold text-slate-800">{stats.total_calls}</h3>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border border-slate-200/60">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Aktive User (30T)</span>
              </div>
              <div className="flex items-end justify-between">
                <h3 className="text-[22px] font-bold text-slate-800">{stats.active_users_30d}</h3>
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {tab === "users" && (
        <Card className="bg-white border border-slate-200/60 shadow-sm overflow-hidden">
          <CardHeader className="pb-0 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-800">Registrierte Benutzer</CardTitle>
          </CardHeader>
          <CardContent className="p-0 mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Firma</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Registriert</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} className="border-b border-slate-100">
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.company_name || "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.role === "admin" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"
                      }`}>{u.role === "admin" ? "Admin" : "Benutzer"}</span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(u.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => viewUser(u.id)} data-testid={`view-user-${u.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Invoices Tab */}
      {tab === "invoices" && (
        <Card className="bg-white border border-slate-200/60 shadow-sm overflow-hidden">
          <CardHeader className="pb-0 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-800">Alle Rechnungen</CardTitle>
          </CardHeader>
          <CardContent className="p-0 mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>Rechnungsnr.</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Firma</TableHead>
                  <TableHead>Netto</TableHead>
                  <TableHead>MwSt.</TableHead>
                  <TableHead>Brutto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Datum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">Keine Rechnungen</TableCell>
                  </TableRow>
                ) : invoices.map(inv => (
                  <TableRow key={inv.id} className="border-b border-slate-100">
                    <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                    <TableCell className="text-sm">{inv.user_name || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{inv.company_name || "-"}</TableCell>
                    <TableCell className="text-sm">{parseFloat(inv.amount).toFixed(2)} €</TableCell>
                    <TableCell className="text-sm">{parseFloat(inv.tax_amount).toFixed(2)} €</TableCell>
                    <TableCell className="text-sm font-semibold">{parseFloat(inv.total_amount).toFixed(2)} €</TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">{inv.status}</span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(inv.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Contact Submissions Tab */}
      {tab === "submissions" && (
        <Card className="bg-white border border-slate-200/60 shadow-sm overflow-hidden">
          <CardHeader className="pb-0 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-800">Kontaktanfragen</CardTitle>
          </CardHeader>
          <CardContent className="p-0 mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Firma</TableHead>
                  <TableHead>Tarif</TableHead>
                  <TableHead>Nachricht</TableHead>
                  <TableHead>Datum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-400">Keine Anfragen</TableCell>
                  </TableRow>
                ) : submissions.map(sub => (
                  <TableRow key={sub.id} className="border-b border-slate-100">
                    <TableCell className="font-medium">{sub.name}</TableCell>
                    <TableCell className="text-sm">{sub.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{sub.company || "-"}</TableCell>
                    <TableCell>
                      {sub.plan && <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#405189]/10 text-[#405189]">{sub.plan}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{sub.message}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(sub.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Benutzerdetails</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Name:</span><p className="font-medium">{selectedUser.name}</p></div>
                <div><span className="text-slate-500">E-Mail:</span><p className="font-medium">{selectedUser.email}</p></div>
                <div><span className="text-slate-500">Firma:</span><p className="font-medium">{selectedUser.company_name || "-"}</p></div>
                <div><span className="text-slate-500">Guthaben:</span><p className="font-semibold text-green-600">{selectedUser.balance?.toFixed(2) || "0.00"} €</p></div>
                <div><span className="text-slate-500">Straße:</span><p className="font-medium">{selectedUser.street || "-"}</p></div>
                <div><span className="text-slate-500">PLZ / Ort:</span><p className="font-medium">{selectedUser.zip_code || ""} {selectedUser.city || "-"}</p></div>
                <div><span className="text-slate-500">Land:</span><p className="font-medium">{selectedUser.country || "-"}</p></div>
                <div><span className="text-slate-500">USt-IdNr.:</span><p className="font-medium">{selectedUser.vat_id || "-"}</p></div>
                <div><span className="text-slate-500">Telefon:</span><p className="font-medium">{selectedUser.phone || "-"}</p></div>
                <div><span className="text-slate-500">Rolle:</span><p className="font-medium">{selectedUser.role}</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
